import { BUILDINGS, BuildingType, RESEARCH_IDS, RECIPES } from '../constants';
import { createGameEngine, resetGameEngine } from '../GameEngine';
import {
  createEmptyPlayerInventory,
  getPlayerInventoryItemCounts,
  setAssemblerRecipe,
} from '../GameState';
import {
  getAssemblerSpeedMultiplier,
  getFactorySpeedMultiplier,
} from '../ResearchSystem';
import { TestDefinition, assert } from '../TestRunner';

const assemblerUpgradeTest: TestDefinition = {
  name: 'Assembler upgrades to factory',
  description:
    'Upgrading an assembler consumes the factory cost, refunds the assembler cost, and preserves the selected recipe.',
  testFunction: async () => {
    resetGameEngine();

    const playerInventory = createEmptyPlayerInventory();
    playerInventory[0] = { itemType: 'iron-plate', count: 100 };
    playerInventory[1] = { itemType: 'gear', count: 100 };
    playerInventory[2] = { itemType: 'circuit', count: 100 };

    const engine = createGameEngine({
      playerInventory,
    });

    engine.unlockResearch(RESEARCH_IDS.BASIC_CIRCUITS, { suppressEvent: true });
    engine.unlockResearch(RESEARCH_IDS.ADVANCED_FACTORIES, {
      suppressEvent: true,
    });

    const assemblerPosition = { x: 30, y: 30 };
    const assemblerPlaced = engine.placeBuilding(
      assemblerPosition.x,
      assemblerPosition.y,
      BUILDINGS.ASSEMBLER
    );

    assert.isTrue(
      assemblerPlaced,
      'Assembler should place successfully before upgrading.'
    );

    let state = engine.getState();
    const assemblerRecipe =
      Object.values(RECIPES).find(
        recipe => recipe.craftingBuilding === BuildingType.ASSEMBLER
      ) ?? null;

    assert.isTrue(
      assemblerRecipe !== null,
      'At least one assembler recipe must be available.'
    );

    if (!assemblerRecipe) {
      throw new Error('Missing assembler recipe for upgrade test.');
    }

    const updatedInventoryLayer = setAssemblerRecipe(
      state.assemblerInventoryLayer,
      state.buildingLayer,
      assemblerPosition,
      assemblerRecipe,
      state.researchState
    );
    engine.setState({ assemblerInventoryLayer: updatedInventoryLayer });

    const result = engine.upgradeAssemblerToFactory(assemblerPosition);
    assert.isTrue(
      result.success,
      'Upgrade should succeed with research unlocked and sufficient items.'
    );

    state = engine.getState();

    const centerKey = `${assemblerPosition.x},${assemblerPosition.y}`;
    assert.equals(
      state.buildingLayer[centerKey],
      BUILDINGS.FACTORY.character,
      'Factory character should replace the assembler footprint.'
    );

    const upgradedInventory = state.assemblerInventoryLayer[centerKey];
    assert.isTrue(
      upgradedInventory?.recipe?.id === assemblerRecipe.id,
      "Factory should retain the assembler's previously selected recipe."
    );

    const inventoryCounts = getPlayerInventoryItemCounts(state.playerInventory);
    assert.equals(
      inventoryCounts['iron-plate'],
      65,
      'Player inventory should reflect the net iron plate cost of the factory.'
    );
    assert.equals(
      inventoryCounts.gear,
      80,
      'Player inventory should reflect the net gear cost of the factory.'
    );
    assert.equals(
      inventoryCounts.circuit,
      80,
      'Player inventory should reflect the circuit cost of the factory.'
    );

    resetGameEngine();

    return {
      name: 'Assembler upgrades to factory',
      passed: true,
      message:
        'Assembler upgraded to a factory, refunded its build cost, and kept the existing recipe selection.',
      duration: 0,
      details: {
        recipe: assemblerRecipe.name,
        inventoryAfterUpgrade: inventoryCounts,
      },
    };
  },
};

const factorySpeedScalingTest: TestDefinition = {
  name: 'Factory inherits assembler speed research',
  description:
    'Factories should benefit from assembler speed upgrades and stack with factory research.',
  testFunction: async () => {
    resetGameEngine();

    const engine = createGameEngine();

    engine.unlockResearch(RESEARCH_IDS.BASIC_CIRCUITS, { suppressEvent: true });
    engine.unlockResearch(RESEARCH_IDS.ADVANCED_FACTORIES, {
      suppressEvent: true,
    });

    engine.unlockResearch(RESEARCH_IDS.ASSEMBLER_OPTIMIZATION, {
      suppressEvent: true,
    });

    let state = engine.getState();
    const assemblerMultiplier = getAssemblerSpeedMultiplier(
      state.researchState
    );
    const factoryMultiplierWithoutResearch = getFactorySpeedMultiplier(
      state.researchState
    );

    assert.equals(
      factoryMultiplierWithoutResearch,
      assemblerMultiplier,
      'Factory speed should include assembler research bonuses before factory upgrades.'
    );

    engine.unlockResearch(RESEARCH_IDS.FACTORY_OPTIMIZATION, {
      suppressEvent: true,
    });

    state = engine.getState();
    const factoryMultiplierWithResearch = getFactorySpeedMultiplier(
      state.researchState
    );

    assert.isTrue(
      factoryMultiplierWithResearch > assemblerMultiplier,
      'Factory research should further increase factory crafting speed.'
    );

    resetGameEngine();

    return {
      name: 'Factory inherits assembler speed research',
      passed: true,
      message:
        'Factory speed scaling includes assembler research and stacks with factory upgrades.',
      duration: 0,
    };
  },
};

export const assemblerUpgradeTests: TestDefinition[] = [
  assemblerUpgradeTest,
  factorySpeedScalingTest,
];
